import { Organization, Portfolio } from '../services/organizationService';

/**
 * Checks if a user can edit organization-level configuration.
 * Org admins (primary or secondary) have full access to org settings.
 * 
 * If no adminConfig is set on the org, access is open to all (backward compatibility).
 * 
 * @param userEmail - The authenticated user's email address
 * @param organization - The organization to check access for
 * @returns true if the user can edit org-level config
 */
export function canEditOrgConfig(userEmail: string, organization: Organization): boolean {
  const admins = organization.adminConfig;
  if (!admins) return true; // No admins configured = open access (backward compat)

  const normalizedEmail = userEmail.toLowerCase().trim();
  const primaryMatch = admins.primaryAdmin?.toLowerCase().trim() === normalizedEmail;
  const secondaryMatch = admins.secondaryAdmin?.toLowerCase().trim() === normalizedEmail;

  return primaryMatch || secondaryMatch;
}

/**
 * Checks if a user can edit a specific portfolio's configuration.
 * 
 * Access is granted if:
 * 1. The user is an org admin (can edit any portfolio), OR
 * 2. The user is listed as a portfolio-level admin (primary or secondary)
 * 
 * If no adminConfig is set on either the org or portfolio, access follows org-level rules.
 * 
 * @param userEmail - The authenticated user's email address
 * @param portfolio - The portfolio to check access for
 * @param organization - The parent organization
 * @returns true if the user can edit this portfolio's config
 */
export function canEditPortfolioConfig(
  userEmail: string,
  portfolio: Portfolio,
  organization: Organization
): boolean {
  // Org admins can always edit any portfolio
  if (canEditOrgConfig(userEmail, organization)) return true;

  // Check portfolio-level admin access
  const admins = portfolio.adminConfig;
  if (!admins) return false; // No portfolio admins and not an org admin = no access

  const normalizedEmail = userEmail.toLowerCase().trim();
  const primaryMatch = admins.primaryAdmin?.toLowerCase().trim() === normalizedEmail;
  const secondaryMatch = admins.secondaryAdmin?.toLowerCase().trim() === normalizedEmail;

  return primaryMatch || secondaryMatch;
}

/**
 * Checks if a user has any admin access at all (org-level or any portfolio-level).
 * Useful for determining whether to show the Settings gear icon.
 * 
 * @param userEmail - The authenticated user's email address
 * @param organization - The organization to check
 * @returns true if the user has admin access to the org or any of its portfolios
 */
export function hasAnyAdminAccess(userEmail: string, organization: Organization): boolean {
  // Check org-level admin
  if (canEditOrgConfig(userEmail, organization)) return true;

  // Check portfolio-level admin for any portfolio
  if (organization.portfolios) {
    return organization.portfolios.some(portfolio =>
      canEditPortfolioConfig(userEmail, portfolio, organization)
    );
  }

  return false;
}

/**
 * Returns the list of portfolios the user can edit.
 * Org admins get all portfolios; portfolio admins get only their own.
 * 
 * @param userEmail - The authenticated user's email address
 * @param organization - The organization to check
 * @returns Array of portfolios the user can edit
 */
export function getEditablePortfolios(
  userEmail: string,
  organization: Organization
): Portfolio[] {
  if (!organization.portfolios) return [];

  // Org admins can edit all portfolios
  if (canEditOrgConfig(userEmail, organization)) {
    return organization.portfolios;
  }

  // Portfolio admins can only edit their own
  return organization.portfolios.filter(portfolio =>
    canEditPortfolioConfig(userEmail, portfolio, organization)
  );
}